import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactDropdown from 'shared/design-system/components/TrueImpactDropdown';
import TrueImpactCheckbox from 'shared/design-system/components/TrueImpactCheckbox';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { AdminProgramQuestion, UpdateNpoReportQuestionInput } from './types';

interface EditQuestionModalProps {
  open: boolean;
  question: AdminProgramQuestion | null;
  isSaving: boolean;
  errorText?: string;
  onSave: (patch: UpdateNpoReportQuestionInput) => unknown;
  onClose: () => unknown;
}

interface DropdownOption {
  id: string;
  label: string;
}

// Kept intentionally small — matches the values the backend accepts today.
const REPORTING_TYPE_OPTIONS: DropdownOption[] = [
  { id: 'full', label: 'Full' },
  { id: 'light', label: 'Light' },
  { id: 'super_light', label: 'Super light' },
];

// All question types extracted from the backend NpoReportQuestion table.
const QUESTION_TYPE_OPTIONS: DropdownOption[] = [
  { id: 'address', label: 'Address' },
  { id: 'beneficiaries', label: 'Beneficiaries' },
  { id: 'checkboxes', label: 'Checkboxes' },
  { id: 'custom_options', label: 'Custom options' },
  { id: 'date', label: 'Date' },
  { id: 'intro', label: 'Intro' },
  { id: 'long_text', label: 'Long text' },
  { id: 'mixed', label: 'Mixed' },
  { id: 'program', label: 'Program' },
  { id: 'program_correct_type', label: 'Program correct type' },
  { id: 'program_multi', label: 'Program multi' },
  { id: 'program_year', label: 'Program year' },
  { id: 'program_year_amount', label: 'Program year amount' },
  { id: 'radio', label: 'Radio' },
  { id: 'service', label: 'Service' },
  { id: 'service_collection', label: 'Service collection' },
  { id: 'tree', label: 'Tree' },
];

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};
  padding: ${SPACERS.large};
  .row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: ${SPACERS.small};
  }
  .field-label {
    margin-bottom: 4px;
  }
  .toggle-row {
    display: flex;
    gap: ${SPACERS.medium};
    align-items: center;
    padding-top: ${SPACERS.tiny};
  }
`;

/**
 * Superadmin-only editor for a single NpoReportQuestion row. Exposes
 * every field allowed by `updateNpoReportQuestion` — including the new
 * `isProgramQuestion` and `reportingType` fields.
 */
const EditQuestionModal: React.FC<EditQuestionModalProps> = ({
  open,
  question,
  isSaving,
  errorText,
  onSave,
  onClose,
}) => {
  const [text, setText] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [section, setSection] = useState<string>('');
  const [helpContent, setHelpContent] = useState<string>('');
  const [tab, setTab] = useState<string>('');
  const [nextQuestionIdRaw, setNextQuestionIdRaw] = useState<string>('');
  const [required, setRequired] = useState<boolean>(false);
  const [sectionStart, setSectionStart] = useState<boolean>(false);
  const [isProgramQuestion, setIsProgramQuestion] = useState<boolean>(false);
  const [reportingType, setReportingType] = useState<
    DropdownOption | undefined
  >(undefined);
  const [questionType, setQuestionType] = useState<DropdownOption | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!question) return;
    setText(question.text ?? '');
    setName(question.name ?? '');
    setSection(question.section ?? '');
    setHelpContent(question.helpContent ?? '');
    setTab(question.tab ?? '');
    setNextQuestionIdRaw(
      question.nextQuestionId != null ? String(question.nextQuestionId) : '',
    );
    setRequired(Boolean(question.required));
    setSectionStart(Boolean(question.sectionStart));
    setIsProgramQuestion(Boolean(question.isProgramQuestion));
    setReportingType(
      REPORTING_TYPE_OPTIONS.find(o => o.id === question.reportingType),
    );
    setQuestionType(
      QUESTION_TYPE_OPTIONS.find(o => o.id === question.questionType),
    );
  }, [question]);

  const handleSave = useCallback(() => {
    if (!question) return;
    const patch: UpdateNpoReportQuestionInput = {
      text: text.trim() || null,
      name: name.trim() || null,
      section: section.trim() || null,
      helpContent: helpContent.trim() || null,
      tab: tab.trim() || null,
      required,
      sectionStart,
      isProgramQuestion,
      reportingType: reportingType?.id ?? null,
      questionType: questionType?.id ?? question.questionType ?? null,
      nextQuestionId: nextQuestionIdRaw.trim()
        ? Number(nextQuestionIdRaw)
        : null,
    };
    onSave(patch);
  }, [
    question,
    text,
    name,
    section,
    helpContent,
    tab,
    required,
    sectionStart,
    isProgramQuestion,
    reportingType,
    questionType,
    nextQuestionIdRaw,
    onSave,
  ]);

  return (
    <TrueImpactModal
      title={
        question ? `Edit question #${question.questionId}` : 'Edit question'
      }
      open={open}
      onClose={onClose}
      maxWidth="1000px"
      next={{
        text: 'Save changes',
        disabled: !question || isSaving,
        isLoading: isSaving,
        onClick: handleSave,
      }}
      previous={{
        text: 'Cancel',
        disabled: isSaving,
        isLoading: false,
        onClick: onClose,
      }}
      errorText={errorText}
    >
      {question && (
        <Body>
          <div>
            <TrueImpactText variant="body-bold" className="field-label">
              Question text
            </TrueImpactText>
            <TrueImpactTextfield
              isTextarea={true}
              value={text}
              onChange={setText}
              placeholder="What is asked of the respondent"
            />
          </div>
          <div className="row">
            <div>
              <TrueImpactText variant="body-bold" className="field-label">
                Short name
              </TrueImpactText>
              <TrueImpactTextfield
                value={name}
                onChange={setName}
                placeholder="Short internal name"
              />
            </div>
            <div>
              <TrueImpactText variant="body-bold" className="field-label">
                Section
              </TrueImpactText>
              <TrueImpactTextfield
                value={section}
                onChange={setSection}
                placeholder="Section label"
              />
            </div>
          </div>
          <div className="row">
            <div>
              <TrueImpactText variant="body-bold" className="field-label">
                Reporting type
              </TrueImpactText>
              <TrueImpactDropdown
                value={reportingType}
                options={REPORTING_TYPE_OPTIONS}
                getId={o => o.id}
                getLabel={o => o.label}
                native={true}
                onChange={setReportingType}
                onClear={undefined}
                getItemDisability={undefined}
                name="Reporting type"
              />
            </div>
            <div>
              <TrueImpactText variant="body-bold" className="field-label">
                Question type
              </TrueImpactText>
              <TrueImpactDropdown
                value={questionType}
                options={QUESTION_TYPE_OPTIONS}
                getId={o => o.id}
                getLabel={o => o.label}
                native={true}
                onChange={setQuestionType}
                onClear={undefined}
                getItemDisability={undefined}
                name="Question type"
              />
            </div>
          </div>
          <div className="row">
            <div>
              <TrueImpactText variant="body-bold" className="field-label">
                Tab
              </TrueImpactText>
              <TrueImpactTextfield
                value={tab}
                onChange={setTab}
                placeholder="Tab key (optional)"
              />
            </div>
            <div>
              <TrueImpactText variant="body-bold" className="field-label">
                Next question ID
              </TrueImpactText>
              <TrueImpactTextfield
                type="number"
                value={nextQuestionIdRaw}
                onChange={setNextQuestionIdRaw}
                placeholder="Leave empty for default"
              />
            </div>
          </div>
          <div>
            <TrueImpactText variant="body-bold" className="field-label">
              Help content
            </TrueImpactText>
            <TrueImpactTextfield
              isTextarea={true}
              value={helpContent}
              onChange={setHelpContent}
              placeholder="Displayed to the respondent as help"
            />
          </div>
          <div className="toggle-row">
            <TrueImpactCheckbox
              checked={required}
              text="Required"
              handleCheck={setRequired}
            />
            <TrueImpactCheckbox
              checked={sectionStart}
              text="Section start"
              handleCheck={setSectionStart}
            />
            <TrueImpactCheckbox
              checked={isProgramQuestion}
              text="Program-level question"
              handleCheck={setIsProgramQuestion}
            />
          </div>
        </Body>
      )}
    </TrueImpactModal>
  );
};

export default EditQuestionModal;
